import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Sparkles, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * TrialBanner — thin dismissible bar shown only while the user is on an active
 * free trial. Surfaces days remaining + a one-click upgrade (biggest lever on
 * trial→paid conversion). Renders nothing for free/paid/logged-out users.
 */
const TrialBanner = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [daysLeft, setDaysLeft] = useState(null);
  // Dismiss for the rest of the day only, so the nudge re-appears daily during
  // the trial (esp. the urgent final stretch) instead of vanishing all session.
  const todayKey = new Date().toISOString().slice(0, 10);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('trial_banner_dismissed_date') === todayKey,
  );

  useEffect(() => {
    if (!user) { setDaysLeft(null); return; }
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/payments/subscription`, { withCredentials: true })
      .then(({ data }) => {
        const sub = data?.subscription;
        if (cancelled || !sub || !sub.is_trial || !sub.expires_at) { setDaysLeft(null); return; }
        const ms = new Date(sub.expires_at).getTime() - Date.now();
        setDaysLeft(ms <= 0 ? 0 : Math.ceil(ms / 86400000));
      })
      .catch(() => { if (!cancelled) setDaysLeft(null); });
    return () => { cancelled = true; };
  }, [user]);

  if (dismissed || daysLeft === null) return null;

  const es = language === 'es';
  // Escalate urgency in the final 3 days — the highest-leverage conversion window.
  const urgent = daysLeft <= 3;
  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem('trial_banner_dismissed_date', todayKey);
  };

  const label = urgent
    ? (daysLeft <= 0
        ? (es ? 'Tu prueba gratuita termina HOY' : 'Your free trial ends TODAY')
        : es
          ? `Solo quedan ${daysLeft} ${daysLeft === 1 ? 'día' : 'días'} de prueba`
          : `Only ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left in your trial`)
    : (es
        ? `Tu prueba gratuita termina en ${daysLeft} ${daysLeft === 1 ? 'día' : 'días'}`
        : `Your free trial ends in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`);

  const subcopy = urgent
    ? (es ? ' — mejora para conservar Strong\u2019s, comentarios y mapas.' : ' — upgrade to keep Strong\u2019s, commentaries & maps.')
    : (es ? ' — mejora para no perder acceso a Slidecon y la IA de sermones.' : ' — upgrade to keep Slidecon + Sermon AI.');

  return (
    <div
      data-testid="trial-banner"
      data-urgent={urgent ? '1' : '0'}
      className={`fixed bottom-0 inset-x-0 z-[60] text-white shadow-2xl bg-gradient-to-r ${
        urgent ? 'from-amber-500 to-red-600' : 'from-emerald-600 to-cyan-700'
      }`}
    >
      <div className="mx-auto max-w-5xl px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className={`w-4 h-4 shrink-0 ${urgent ? 'animate-pulse' : ''}`} />
          <span className="text-sm font-medium truncate">
            {label}
            <span className="hidden sm:inline text-white/80 font-normal">
              {subcopy}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            data-testid="trial-banner-upgrade-btn"
            onClick={() => navigate(urgent && daysLeft > 0 ? '/pricing?promo=TRIAL20' : '/pricing')}
            className={`rounded-full bg-white text-sm font-semibold px-4 py-1.5 transition-colors ${
              urgent ? 'text-red-700 hover:bg-red-50' : 'text-emerald-700 hover:bg-emerald-50'
            }`}
          >
            {urgent && daysLeft > 0
              ? (es ? 'Mejorar con 20% menos' : 'Upgrade — 20% off')
              : (es ? 'Mejorar ahora' : 'Upgrade now')}
          </button>
          <button
            data-testid="trial-banner-dismiss-btn"
            onClick={dismiss}
            className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
